import { FormEvent, useState } from 'react';
import Reveal from './Reveal';
import { api } from '@/lib/api';
import { track } from '@/lib/analytics';
import { STUDIO } from '@/content/studio';

/**
 * Band 7 — NEWSLETTER. Red closer before the black footer.
 * Subscribes via /api/subscribe with source "homepage".
 */

type FormState = 'idle' | 'loading' | 'success' | 'error';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const NewsletterBand = () => {
    const [email, setEmail] = useState('');
    const [state, setState] = useState<FormState>('idle');
    const [message, setMessage] = useState<string | null>(null);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (state === 'loading') return;

        const trimmed = email.trim();
        if (!EMAIL_RE.test(trimmed)) {
            setState('error');
            setMessage('That email doesn’t look right. Try again.');
            return;
        }

        setState('loading');
        setMessage(null);
        const res = await api.subscribe(trimmed, 'homepage');
        if (res.error) {
            setState('error');
            setMessage(res.error);
        } else {
            setState('success');
            track('newsletter_subscribe', { source: 'homepage' });
            setMessage('You’re on the list.');
            setEmail('');
        }
    };

    return (
        <section className="hb-news" aria-label="Newsletter signup">
            <Reveal>
                <h2 className="hb-news-heading">{STUDIO.newsletterHeading}</h2>
                <p className="hb-news-sub">{STUDIO.newsletterSub}</p>

                {state === 'success' ? (
                    <p className="hb-news-success" role="status">
                        {message} <span aria-hidden="true">✓</span>
                    </p>
                ) : (
                    <form className="hb-news-form" onSubmit={handleSubmit} noValidate>
                        <label className="hb-news-srlabel" htmlFor="hb-news-email">
                            Email address
                        </label>
                        <input
                            id="hb-news-email"
                            type="email"
                            className="hb-news-input"
                            placeholder="your@email.com"
                            value={email}
                            autoComplete="email"
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (state === 'error') {
                                    setState('idle');
                                    setMessage(null);
                                }
                            }}
                            disabled={state === 'loading'}
                        />
                        <button
                            type="submit"
                            className="hb-news-button"
                            disabled={state === 'loading'}
                        >
                            {state === 'loading' ? 'SENDING…' : 'SIGN UP →'}
                        </button>
                    </form>
                )}
                {state === 'error' && message && (
                    <p className="hb-news-error" role="alert">
                        {message}
                    </p>
                )}
            </Reveal>

            <style>{`
                .hb-news {
                    background: var(--et-bg-2);
                    padding: var(--by) var(--bx);
                }
                .hb-news-heading {
                    font-family: var(--font-display);
                    font-weight: 700;
                    font-size: clamp(3.4rem, 11vw, 10rem);
                    line-height: 0.9;
                    text-transform: uppercase;
                    color: var(--paper);
                    margin: 0 0 clamp(1rem, 2.5vw, 1.6rem);
                    text-wrap: balance;
                }
                .hb-news-sub {
                    font-family: var(--font-family);
                    font-size: clamp(0.98rem, 1.5vw, 1.2rem);
                    font-weight: 500;
                    line-height: 1.55;
                    color: var(--et-text);
                    max-width: 46ch;
                    margin: 0 0 clamp(2rem, 4.5vw, 3rem);
                }
                .hb-news-form {
                    display: flex;
                    max-width: 620px;
                    border: 2px solid rgba(244, 242, 238, 0.55);
                }
                .hb-news-srlabel {
                    position: absolute;
                    width: 1px;
                    height: 1px;
                    overflow: hidden;
                    clip: rect(0 0 0 0);
                    white-space: nowrap;
                }
                .hb-news-input {
                    flex: 1;
                    min-width: 0;
                    min-height: 56px;
                    padding: 0 1.1rem;
                    background: transparent;
                    border: none;
                    outline: none;
                    font-family: var(--font-family);
                    font-size: 1rem;
                    font-weight: 600;
                    color: var(--paper);
                }
                .hb-news-input::placeholder { color: rgba(244, 242, 238, 0.45); }
                .hb-news-input:focus { background: rgba(244, 242, 238, 0.08); }
                /* Surgical red accent — the submit button. */
                .hb-news-button {
                    min-height: 56px;
                    padding: 0 clamp(1.2rem, 3vw, 2.2rem);
                    background: var(--color-accent-primary);
                    border: none;
                    cursor: pointer;
                    font-family: var(--font-family);
                    font-size: 0.82rem;
                    font-weight: 700;
                    letter-spacing: 0.16em;
                    color: #fff;
                    white-space: nowrap;
                    transition: background var(--transition-fast);
                }
                .hb-news-button:hover:not(:disabled) { background: var(--color-accent-secondary); }
                .hb-news-button:disabled { opacity: 0.7; cursor: wait; }
                .hb-news-success {
                    font-family: var(--font-display);
                    font-weight: 700;
                    font-size: clamp(1.8rem, 4vw, 3rem);
                    line-height: 1;
                    text-transform: uppercase;
                    color: var(--paper);
                    border: 2px solid var(--color-accent-primary);
                    display: inline-block;
                    padding: 1rem 1.6rem;
                    margin: 0;
                }
                .hb-news-error {
                    font-family: var(--font-mono);
                    font-size: 0.74rem;
                    font-weight: 700;
                    letter-spacing: 0.12em;
                    color: #fff;
                    background: var(--color-accent-primary);
                    display: inline-block;
                    padding: 0.5rem 0.8rem;
                    margin: 0.9rem 0 0;
                }

                @media (max-width: 520px) {
                    .hb-news-form { flex-direction: column; border: none; gap: 0.8rem; }
                    .hb-news-input { border: 2px solid rgba(244, 242, 238, 0.55); min-height: 52px; }
                    .hb-news-button { min-height: 52px; }
                }
            `}</style>
        </section>
    );
};

export default NewsletterBand;
